import React, { useEffect, useState } from "react";
import { TeamsAndSalesData } from "../../../Interfaces/TeamAdminCoordDashboard";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
  LabelList,
} from "recharts";

interface FormattedData {
  name: string;
  sales: number;
  color: string;
}

interface TeamAndSalesProps {
  primary_div: TeamsAndSalesData[];
  secondary_div: TeamsAndSalesData[];
  primary_div_label?: string;
  secondary_div_label?: string;
  width: number;
  height: number;
}

const TeamAndSales: React.FC<TeamAndSalesProps> = ({
  primary_div,
  secondary_div,
  primary_div_label = "",
  secondary_div_label = "",
  width,
  height,
}) => {
  const [fontSize, setFontSize] = useState(12); // Default font size
  const MAX_BAR_GRAPH_ENTERIES = 8; // Maximum number of entries to display

  const [formattedData, setFormattedData] = useState<FormattedData[]>(
    primary_div.map((team) => ({
      name: team.name,
      sales: team.sales,
      color: `rgba(83, 143, 200, ${team.opacity})`, // blue
    }))
  );
  const [legendInfo, setLegendInfo] = useState<FormattedData>({
    name: "primary divisions",
    sales: 0,
    color: "rgba(83, 143, 200, 1)", // blue
  });
  console.log(formattedData);
  const [highlightDivName, setHighlightDivName] = useState(primary_div_label);
  const [selectedCategory, setSelectedCategory] = useState<
    "Primary Division" | "Secondary Division"
  >("Primary Division");

  const handleCategoryChange = (
    category: "Primary Division" | "Secondary Division"
  ) => {
    setSelectedCategory(category);
    switch (category) {
      case "Primary Division":
        setFormattedData(
          primary_div.map((team) => ({
            name: team.name,
            sales: team.sales,
            color: `rgba(83, 143, 200, ${team.opacity})`, // blue
          }))
        );
        setLegendInfo({
          name: "primary divisions",
          sales: 0,
          color: "rgba(83, 143, 200, 1)", // blue
        });
        setHighlightDivName(primary_div_label);
        break;
      case "Secondary Division":
        setFormattedData(
          secondary_div.map((team) => ({
            name: team.name,
            sales: team.sales,
            color: `rgba(91, 189, 196, ${team.opacity})`, // teal
          }))
        );
        setLegendInfo({
          name: "secondary divisions",
          sales: 0,
          color: "rgba(91, 189, 196, 1)", // blue
        });
        setHighlightDivName(secondary_div_label);
        break;
    }
  };

  const updateWidth = () => {
    if (width > 0 && height > 0) {
      const newFontSize = Math.max(width / 16, 6); // Set a minimum font size of 12
      setFontSize(newFontSize);
    }
  };

  useEffect(() => {
    // Initial update on component mount + tab change
    updateWidth();
    handleCategoryChange("Primary Division"); //so it resets when we change tabs
  }, [primary_div, secondary_div]);

  useEffect(() => {
    // update on height and width change, dont want to change division back to default tho
    updateWidth();
  }, [width, height]);

  const dynamicBarSize = Math.max(width / formattedData.length / 4, 10); // Ensure a minimum size

  return (
    <div style={{ width: "100%", margin: "0 auto", display: "block" }}>
      <header
        style={{
          position: "absolute",
          top: "2%",
          left: "3%",
          fontWeight: "700",
          paddingBottom: "5%",
          paddingTop: "5%",
          fontSize: fontSize * 1.25,
        }}
      >
        SELLERS & TEAMS
      </header>
      <div style={{ position: "absolute", top: width * 0.05, right: "3%" }}>
        <select
          value={selectedCategory}
          onChange={(e) =>
            handleCategoryChange(
              e.target.value as "Primary Division" | "Secondary Division"
            )
          }
          style={{
            fontSize: fontSize * 0.8,
            padding: "5px",
            borderRadius: "5px",
            backgroundColor: "transparent", // Transparent background
            border: "1px solid rgba(0, 0, 0, 0)", // Subtle border to make it look nice
            color: "#000", // Text color (black)
            appearance: "auto", // Remove default styling (for some browsers)
            outline: "none", // Remove outline for a cleaner look
          }}
        >
          <option value="Primary Division">Primary</option>
          <option value="Secondary Division">Secondary</option>
        </select>
      </div>
      <div style={{ width: width, height: height, paddingTop: "5%" }}>
        <ResponsiveContainer>
          <BarChart
            data={formattedData.slice(0, MAX_BAR_GRAPH_ENTERIES)}
            margin={{
              top: fontSize,
              right: 5,
              left: 0,
              bottom: 0,
            }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="name"
              tick={({ x, y, payload }) => {
                return (
                  <text
                    x={x}
                    y={y * 1.05}
                    textAnchor="middle"
                    fill="black"
                    fontSize={fontSize * 0.6}
                    fontWeight={
                      payload.value === highlightDivName ? "bold" : "normal"
                    } // Bold "team green" label
                  >
                    {payload.value}
                  </text>
                );
              }}
              // tickSize={10}
              padding={{ left: 0, right: 0 }}
            />
            <YAxis
              width={width * 0.14}
              padding={{ top: 0, bottom: 0 }}
              tick={{ fontSize: fontSize * 0.7 }}
            />
            <Tooltip
              formatter={(value: number | string, name: string, props: any) => [
                `$${value}`,
                "Sales",
              ]}
              contentStyle={{
                fontSize: fontSize * 0.8,
              }}
              labelStyle={{
                fontSize: fontSize * 0.8,
              }}
            />
            <Legend
              iconType="circle"
              payload={[
                {
                  value: legendInfo.name,
                  type: "circle",
                  color: legendInfo.color, // could also decide to change this color if its the name so it pops more
                },
              ]}
              wrapperStyle={{
                fontSize: fontSize,
                paddingTop: "1%",
              }}
            />
            <Bar dataKey="sales" barSize={dynamicBarSize}>
              <LabelList
                dataKey="sales"
                position="top"
                style={{ fontSize: fontSize * 0.65 }}
                formatter={(value: string) => `$${value}`}
              />
              {formattedData
                .slice(0, MAX_BAR_GRAPH_ENTERIES)
                .map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
      <div
        style={{
          paddingTop: "0%",
          position: "absolute",
          bottom: "2%",
          left: "12%",
        }}
      >
        <p
          style={{
            fontSize: fontSize * 0.6,
            color: "rgba(0, 0, 0, .5)",
            margin: 0, // Remove default margin of <p> tag
          }}
        >
          The color saturation of bars decreases with ranking.
        </p>
      </div>
    </div>
  );
};

export default TeamAndSales;
